/**
 * Servicio para gestión de plantillas de documentos (SGC - ISO 9001:2015)
 * Consume los endpoints del backend para CRUD de plantillas y generación de documentos
 */

import api from '../utils/api';
import logger from '../utils/logger';

/**
 * Obtener todas las plantillas con filtros opcionales
 * @param {Object} filters - Filtros opcionales { status, process, search }
 * @returns {Promise<Array>} Lista de plantillas
 */
export const getAllTemplates = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.process) params.append('process', filters.process);
    if (filters.search) params.append('search', filters.search);

    const response = await api.get(`/document-templates?${params.toString()}`);
    return response.data;
  } catch (error) {
    logger.error('Error fetching document templates:', error);
    throw error;
  }
};

/**
 * Obtener una plantilla específica por ID
 * @param {string} id - UUID de la plantilla
 * @returns {Promise<Object>} Datos de la plantilla
 */
export const getTemplateById = async (id) => {
  try {
    const response = await api.get(`/document-templates/${id}`);
    return response.data;
  } catch (error) {
    logger.error(`Error fetching template ${id}:`, error);
    throw error;
  }
};

/**
 * Crear una nueva plantilla de documento
 * @param {Object} templateData - Datos de la plantilla
 * @returns {Promise<Object>} Plantilla creada
 */
export const createTemplate = async (templateData) => {
  try {
    const response = await api.post('/document-templates', templateData);
    return response.data;
  } catch (error) {
    logger.error('Error creating template:', error);
    throw error;
  }
};

/**
 * Actualizar una plantilla existente
 * @param {string} id - UUID de la plantilla
 * @param {Object} templateData - Datos a actualizar
 * @returns {Promise<Object>} Plantilla actualizada
 */
export const updateTemplate = async (id, templateData) => {
  try {
    const response = await api.put(`/document-templates/${id}`, templateData);
    return response.data;
  } catch (error) {
    logger.error(`Error updating template ${id}:`, error);
    throw error;
  }
};

/**
 * Eliminar una plantilla (soft delete - marca como obsolete)
 * @param {string} id - UUID de la plantilla
 * @returns {Promise<void>}
 */
export const deleteTemplate = async (id) => {
  try {
    await api.delete(`/document-templates/${id}`);
  } catch (error) {
    logger.error(`Error deleting template ${id}:`, error);
    throw error;
  }
};

/**
 * Obtener historial de cambios de una plantilla
 * @param {string} id - UUID de la plantilla
 * @returns {Promise<Array>} Lista de cambios
 */
export const getTemplateChanges = async (id) => {
  try {
    const response = await api.get(`/document-templates/${id}/changes`);
    return response.data;
  } catch (error) {
    logger.error(`Error fetching changes for template ${id}:`, error);
    throw error;
  }
};

/**
 * Incrementar manualmente el consecutivo de una plantilla
 * @param {string} id - UUID de la plantilla
 * @param {number} nextNumber - Nuevo número consecutivo
 * @returns {Promise<Object>} Plantilla actualizada
 */
export const incrementTemplateNumber = async (id, nextNumber) => {
  try {
    const response = await api.post(`/document-templates/${id}/increment-number`, {
      nextNumber
    });
    return response.data;
  } catch (error) {
    logger.error(`Error incrementing number for template ${id}:`, error);
    throw error;
  }
};

/**
 * Generar un documento basado en una plantilla y reserva
 * @param {string} templateId - UUID de la plantilla
 * @param {string} reservationId - UUID de la reserva
 * @param {Object} customSections - Secciones personalizadas a incluir (opcional)
 * @returns {Promise<Object>} Documento generado { documentNumber, htmlContent, template, generatedAt }
 */
export const generateDocument = async (templateId, reservationId, customSections = null) => {
  try {
    const payload = {
      templateId,
      reservationId
    };

    // Si se proporcionan secciones personalizadas, incluirlas en el payload
    if (customSections) {
      payload.customSections = customSections;
    }

    const response = await api.post('/document-templates/generate', payload);
    return response.data;
  } catch (error) {
    logger.error('Error generating document:', error);
    throw error;
  }
};

/**
 * Abrir documento generado en nueva ventana
 * @param {string} htmlContent - HTML del documento
 * @param {string} documentNumber - Número del documento
 */
export const openDocumentWindow = (htmlContent, documentNumber) => {
  const newWindow = window.open('', '_blank', 'width=900,height=1100');

  if (!newWindow) {
    alert('Por favor, permite ventanas emergentes para generar documentos');
    return;
  }

  newWindow.document.write(htmlContent);
  newWindow.document.title = documentNumber || 'Documento';
  newWindow.document.close();
};

export default {
  getAllTemplates,
  getTemplateById,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  getTemplateChanges,
  incrementTemplateNumber,
  generateDocument,
  openDocumentWindow
};
